import * as React from 'react'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import PreviewIcon from '@mui/icons-material/Preview'
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowId,
  GridToolbar,
} from '@mui/x-data-grid'
import { useNavigate } from 'react-router-dom'
import { ActivityModel } from 'types'
import axios from 'axios'
import { base_url } from 'env'
import { toast } from 'react-hot-toast'
import { Box } from '@mui/material'
import SearchBar from './SearchBar'
import ExportButton from './ExportButton'

const searchFields = [
  { value: 'title', label: 'Titre' },
  { value: 'category', label: 'Catégorie' },
  { value: 'support', label: 'Support' },
  { value: 'baseDifficulty', label: 'Difficulté' },
  { value: 'basePleasure', label: 'Plaisir' },
  { value: 'id', label: 'ID' },
]

export default function ActivityDataGrid({
  activities,
}: {
  activities: ActivityModel[]
}) {
  const [rows, setRows] = React.useState(activities)
  const navigate = useNavigate()

  React.useEffect(() => {
    setRows(activities)
  }, [activities])

  const handleDeleteClick = (id: GridRowId) => () => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer cette activité ?`)) {
      axios
        .delete(base_url + `/activity/${id}`)
        .then(() => {
          toast.success('Activité supprimée avec succès.')
          setRows((prevRows) => prevRows.filter((row) => row.id !== id))
        })
        .catch((err) => {
          console.error(err)
        })
    }
  }

  const handleEditClick = (id: GridRowId) => () => {
    navigate(`/activity/${id}/edit`)
  }

  const handlePreviewClick = (id: GridRowId) => () => {
    navigate(`/activity/${id}`)
  }

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 250 },
    { field: 'title', headerName: 'Titre', flex: 1, minWidth: 200 },
    { field: 'category', headerName: 'Catégorie', width: 130 },
    { field: 'support', headerName: 'Support', width: 150 },
    {
      field: 'baseDifficulty',
      headerName: 'Difficulté',
      type: 'number',
      width: 100,
    },
    {
      field: 'basePleasure',
      headerName: 'Plaisir',
      type: 'number',
      width: 100,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            icon={<PreviewIcon />}
            label="Aperçu"
            onClick={handlePreviewClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Modifier"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Supprimer"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ]
      },
    },
  ]

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          margin: '1rem',
          gap: '1rem',
        }}
      >
        <SearchBar
          onSearch={setRows}
          fields={searchFields}
          originalData={activities}
        />
        <ExportButton
          fields={[
            'id',
            'title',
            'category',
            'support',
            'baseDifficulty',
            'basePleasure',
            'instructions',
            'instructionsCloseCircle',
            'instructionsOther',
          ]}
          data={activities}
          exportFileName="activities_export"
        />
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        slots={{ toolbar: GridToolbar }}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        checkboxSelection
        disableRowSelectionOnClick
      />
    </Box>
  )
}
